import { useAppSelector } from 'store/hooks';
import { Box, Grid, Paper, Typography } from '@mui/material';
import { IOrder } from 'components/order/model/Order';
import { orderApi } from 'components/order/orderApi';
import { OrderStateChip } from 'components/order/order-details/OrderStateChip';
import { ORDERS } from 'config/routes';
import { MainLayout } from 'layouts/MainLayout';
import { LoadingTypography } from 'lib/LoadingTypoghraphy';
import logger from 'lib/logger';
import { isEmptyString, isString } from 'lib/typeguards';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Row } from 'ui/Flex';
import { OrderSummary } from 'components/order/order-details/OrderSummary';
import { OrderPaymentsTimeline } from 'components/order/order-details/OrderPaymentsTimeline';
import { LoadingButton } from 'ui/buttons/LoadingButton';
import { useRefundAll } from 'components/order/hooks/useRefundAll';
import { PaymentRefundConfirmationDialog } from 'components/order/order-details/PaymentRefundConfirmationDialog';
import { Permission, Resource, permission } from '@pepperhq/auth-client';
import { useForceClose } from 'components/order/hooks/useForceClose';
import { ActionDialog } from 'ui/dialogs/ActionDialog';
import { formatDateTime } from 'lib/timeHelpers';
import { OrderOverview } from 'components/order/order-details/OrderOverview';

export const OrderViewPage = () => {
    const { orderId } = useParams<{ orderId?: string }>();
    const { claims } = useAppSelector(state => state.auth);
    const [loading, setLoading] = React.useState(true);
    const [order, setOrder] = React.useState<IOrder>();
    const navigate = useNavigate();

    const loadOrder = React.useCallback(() => {
        const initLoad = async () => {
            if (isString(orderId) && !isEmptyString(orderId)) {
                const result = await orderApi.get(orderId);
                if (!result.ok) {
                    logger.error(result.body.message);
                    return navigate(ORDERS, { replace: true });
                }
                setOrder(result.body);
            }
            setLoading(false);
        };
        initLoad();
    }, [navigate, orderId]);
    const { onRefundAll, isRefundable, refundAllLoading, modalProps } = useRefundAll(order, loadOrder);
    const {
        isLoading,
        onForceClose,
        isClosable,
        modalProps: closeModalProps
    } = useForceClose(order, loadOrder);
    React.useEffect(() => {
        loadOrder();
    }, [loadOrder, orderId]);

    const hasRefundPermission = React.useMemo(
        () => claims.hasPermissions(permission(Resource.OrderRefund, Permission.all)),
        [claims]
    );
    const forceCloseContent = React.useMemo(
        () =>
            // eslint-disable-next-line max-len
            `Please be aware you will be permanently closing order ${order?.id} and this action is irreversible. To confirm closure, type 'FORCE CLOSE' in the text input field.`,
        [order?.id]
    );
    const isRefundEnabled = hasRefundPermission && !order?.isOpen;
    return (
        <MainLayout
            breadcrumbs={[{ label: 'Orders', url: ORDERS }]}
            pageName={orderId}
            pageDescription="View and interact with selected order."
        >
            <Grid container spacing={2}>
                <Grid size={12}>
                    <Paper>
                        <Box padding={2}>
                            <Row flex={1} align="space-between" valign="center">
                                <Row valign="center" gutter>
                                    <Box>
                                        <Typography variant="h6">Overview</Typography>
                                        <LoadingTypography
                                            gutterBottom
                                            variant="caption"
                                            width={300}
                                            loading={loading}
                                        >
                                            {formatDateTime(new Date(order?.createdAt))}
                                        </LoadingTypography>
                                    </Box>
                                    {order && <OrderStateChip order={order} />}
                                </Row>
                                {isClosable && (
                                    <LoadingButton
                                        onClick={onForceClose}
                                        loading={isLoading}
                                        variant="outlined"
                                        color="primary"
                                    >
                                        Force Close
                                    </LoadingButton>
                                )}
                                {isRefundable && isRefundEnabled && (
                                    <LoadingButton
                                        onClick={onRefundAll}
                                        loading={refundAllLoading}
                                        variant="outlined"
                                        color="primary"
                                    >
                                        Refund All
                                    </LoadingButton>
                                )}
                            </Row>
                            <OrderOverview order={order} loading={loading} />
                        </Box>
                    </Paper>
                </Grid>
                <Grid
                    size={{
                        xs: 12,
                        lg: 7
                    }}
                >
                    <Paper>
                        <Box padding={2}>
                            <Typography variant="h6" sx={{ pb: 2 }}>
                                Payments
                            </Typography>
                            <OrderPaymentsTimeline
                                order={order}
                                loading={loading}
                                onRefundSuccess={loadOrder}
                                isRefundEnabled={isRefundEnabled}
                            />
                        </Box>
                    </Paper>
                </Grid>
                <Grid
                    size={{
                        xs: 12,
                        lg: 5
                    }}
                >
                    <Paper>
                        <Box padding={2}>
                            <Typography variant="h6" sx={{ pb: 2 }}>
                                Order Details
                            </Typography>
                            <OrderSummary order={order} loading={loading} />
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
            <PaymentRefundConfirmationDialog {...modalProps} />
            <ActionDialog
                open={closeModalProps.open}
                onConfirm={closeModalProps.onAgree}
                onClose={closeModalProps.onClose}
                loading={closeModalProps.loading}
                submitLabel="Force Close"
                title="Force close order"
                content={forceCloseContent}
                protectionWord="FORCE CLOSE"
            />
        </MainLayout>
    );
};
